import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { uploadPhoto } from "@/lib/uploadPhoto";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";
import { AlendraInlineButton } from "@/components/AlendraAI";
import AlendraAI from "@/components/AlendraAI";
import { useAuth, hasCapability, hasRole } from "@/context/AuthContext";
import { useSpecSchemas } from "@/lib/specSchemas";
import SpecFields from "@/components/SpecFields";
import PublishOfferDialog from "@/components/products/PublishOfferDialog";
import PriceAIPanel from "@/components/products/PriceAIPanel";
import CoverPhotoField from "@/components/products/CoverPhotoField";
import { storefrontReadiness } from "@/lib/readiness";
import { MagnifyingGlass, Package, Plus, Check, CaretDown, Camera, Trash, FirstAidKit, ListChecks, Sparkle, Spinner, X as XIcon, CurrencyDollar, Storefront } from "@phosphor-icons/react";

const EMPTY = {
  name: "", category: "", brand: "", sku: "", stock: 0,
  cost_currency: "USD", sale_currency: "MXN",
  cost: 0, min_price: 0, reseller_price: 0, marketplace_price: 0, store_price: 0,
  characteristics: "", low_stock_threshold: 3, photos_url: "", photos: [], cover_photo: "",
  free_shipping: false, shipping_cost_mxn: "", shipping_cost_bc_mxn: "", condition_notes: "",
  condition: "seminuevo", aesthetic_grade: "", aesthetic_notes: "", aesthetic_photos: [],
  // Storefront enrichment v2
  specs: {}, use_cases: [], short_tagline: "",
  compare_at_price: 0, badges: [],
  show_in_storefront: false, storefront_priority: 0,
};

// Ficha técnica por categoría: esquema único servido por /api/spec-schemas (ver core/spec_schemas.py).

const DERIVED_BADGE_CANDIDATES = [
  { key: "nuevo",           label: "Nuevo",           color: "#00873D" },
  { key: "promo",           label: "Promo",           color: "#E0000F" },
  { key: "hot",             label: "Hot",             color: "#F97316" },
  { key: "envio_gratis",    label: "Envío gratis",    color: "#059669" },
  { key: "ultimas_piezas",  label: "Últimas piezas",  color: "#DB2777" },
  { key: "reacondicionado", label: "Reacondicionado", color: "#7C3AED" },
];

const ensureHttps = (u) => {
  if (!u) return "";
  const trimmed = String(u).trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed.replace(/^\/+/, "")}`;
};


// Sección numerada del formulario (hoisted: si se define dentro del componente,
// React la remonta en cada render y los inputs pierden el foco al escribir).
function FormSection({ id, n, icon: Icon, title, subtitle, tint, right, children, testid, done }) {
  return (
    <section id={`pf-${id}`} data-testid={testid || `form-section-${id}`} className={`rounded-lg border p-5 space-y-4 shadow-sm scroll-mt-4 ${tint ? "border-2 border-[#002FA7]/30 bg-gradient-to-br from-[#F5F8FF] to-white" : "border-zinc-200 bg-white"}`}>
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <span className={`w-8 h-8 rounded-md flex items-center justify-center font-heading font-extrabold text-sm shrink-0 ${done ? "bg-emerald-100 text-emerald-700" : "bg-[#002FA7]/10 text-[#002FA7]"}`}>
            {done ? <Check size={16} weight="bold" /> : n}
          </span>
          <div>
            <h3 className="font-heading font-bold text-base text-zinc-900 flex items-center gap-1.5"><Icon size={16} weight="duotone" className="text-[#002FA7]" /> {title}</h3>
            <p className="text-xs text-zinc-500">{subtitle}</p>
          </div>
        </div>
        {right}
      </header>
      {children}
    </section>
  );
}

export default function ProductForm({ product, categories, onClose, onSaved, onCategoriesChanged, onSelectExisting, draftMode = false, onSubmitDraft, draftTitle, draftSubmitLabel, initialStep = null }) {
  const { user } = useAuth();
  const canUseAlendra = hasRole(user, "super_admin") || hasCapability(user, "use_alendra_ai");
  const [form, setForm] = useState(product ? {
    ...EMPTY,
    ...product,
    photos: Array.isArray(product.photos) ? product.photos : (product.photos_url ? [product.photos_url] : []),
    cover_photo: product.cover_photo || "",
    shipping_cost_mxn: product.shipping_cost_mxn ?? "",
    shipping_cost_bc_mxn: product.shipping_cost_bc_mxn ?? "",
    condition_notes: product.condition_notes || "",
    aesthetic_grade: product.aesthetic_grade || "",
    aesthetic_photos: Array.isArray(product.aesthetic_photos) ? product.aesthetic_photos : [],
    specs: (product.specs && typeof product.specs === "object") ? product.specs : {},
    use_cases: Array.isArray(product.use_cases) ? product.use_cases : [],
    badges: Array.isArray(product.badges) ? product.badges : [],
    compare_at_price: Number(product.compare_at_price) || 0,
    storefront_priority: Number(product.storefront_priority) || 0,
  } : { ...EMPTY, category: categories[0] || "" });
  const [saving, setSaving] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeStep, setActiveStep] = useState("basic");
  const contentRef = useRef(null);
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const ids = ["basic", "specs", "stock", "photos", "store"];
    const els = ids.map((id) => document.getElementById(`pf-${id}`)).filter(Boolean);
    const obs = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (vis[0]) setActiveStep(vis[0].target.id.replace("pf-", ""));
    }, { root, rootMargin: "-10% 0px -70% 0px" });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!initialStep) return;
    const t = setTimeout(() => {
      const root = contentRef.current;
      const el = document.getElementById(`pf-${initialStep}`);
      if (el && root) { root.scrollTop = el.offsetTop - root.offsetTop - 8; setActiveStep(initialStep); }
    }, 600);
    return () => clearTimeout(t);
  }, [initialStep]);
  // ----- brand combobox state -----
  const [brands, setBrands] = useState([]); // [{name, count}]
  const [brandOpen, setBrandOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState("");
  // ----- duplicate-detection state (only when creating, not editing) -----
  const isNew = !product;
  const [allProducts, setAllProducts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const nameWrapRef = useRef(null);
  // ----- Send-to-workshop dialog state -----
  const [tallerOpen, setTallerOpen] = useState(false);
  const [tallerForm, setTallerForm] = useState({ fault_description: "", location: "", priority: "normal" });
  const [tallerSending, setTallerSending] = useState(false);
  const canSendToTaller = !!product && !draftMode && (hasRole(user, "super_admin") || hasRole(user, "inventory") || hasRole(user, "technician") || hasRole(user, "taller"));
  const availableStock = product ? (Number(product.stock || 0) - Number(product.hospital_qty || 0)) : 0;

  const sendToTaller = async () => {
    if (!tallerForm.fault_description.trim()) {
      toast.error("Describe la falla");
      return;
    }
    setTallerSending(true);
    try {
      await api.post("/hospital/tickets", {
        product_id: product.id,
        reason: tallerForm.fault_description.trim(),
        fault_description: tallerForm.fault_description.trim(),
        location: tallerForm.location.trim() || null,
        priority: tallerForm.priority,
        source: "piso_de_venta",
      });
      toast.success("Equipo enviado al taller · stock bloqueado");
      setTallerOpen(false);
      setTallerForm({ fault_description: "", location: "", priority: "normal" });
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar al taller");
    } finally {
      setTallerSending(false);
    }
  };

  useEffect(() => {
    api.get("/products/brands").then((r) => setBrands(r.data || [])).catch(() => {});
  }, []);

  // Storefront enrichment · usos ideales editables
  const [useCases, setUseCases] = useState([]);
  const [newUseCase, setNewUseCase] = useState("");
  const reloadUseCases = () =>
    api.get("/products/use-cases").then((r) => setUseCases(r.data || [])).catch(() => setUseCases([]));
  useEffect(() => { reloadUseCases(); }, []);

  const addCustomUseCase = async () => {
    const label = newUseCase.trim();
    if (!label) return;
    const key = label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_").slice(0, 40);
    try {
      await api.post("/products/use-cases", { key, label });
      setNewUseCase("");
      reloadUseCases();
    } catch (err) { toast.error(err?.response?.data?.detail || "No se pudo agregar"); }
  };

  const toggleUseCase = (key) => {
    const has = (form.use_cases || []).includes(key);
    set("use_cases", has ? form.use_cases.filter((k) => k !== key) : [...(form.use_cases || []), key]);
  };

  const specSchemas = useSpecSchemas();
  const specTemplate = specSchemas.schemaFor(form.category);
  const specProgress = specSchemas.progress(form.category, form.specs);
  // Siempre el form más reciente: permite encadenar varias sugerencias de IA sin closures viejos.
  const formRef = useRef(form);
  useEffect(() => { formRef.current = form; }, [form]);
  const [aiSpecs, setAiSpecs] = useState({ keys: [], confidence: {}, notes: "", loading: false });
  const suggestSpecs = async () => {
    if (aiSpecs.loading) return;
    const f = formRef.current;
    if (!f.name?.trim()) { toast.error("Escribe el nombre del producto para sugerir la ficha."); return; }
    setAiSpecs((a) => ({ ...a, loading: true }));
    try {
      const payload = { name: f.name, brand: f.brand, category: f.category, characteristics: f.characteristics, aesthetic_notes: f.aesthetic_notes, condition: f.condition, specs: f.specs || {} };
      const { data } = await api.post("/alendra/suggest-specs-form", payload);
      const keys = Object.keys(data.specs || {});
      if (keys.length === 0) { toast.info(data.notes || "Alendra no encontró datos suficientes para sugerir más campos."); return false; }
      setForm((s) => ({ ...s, specs: { ...(s.specs || {}), ...data.specs } }));
      setAiSpecs((a) => ({ keys: [...new Set([...a.keys, ...keys])], confidence: { ...a.confidence, ...(data.confidence || {}) }, notes: data.notes || "", loading: false }));
      toast.success(`Alendra sugirió ${keys.length} campo(s). Revísalos antes de guardar ✨`);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo sugerir la ficha técnica");
      return false;
    } finally { setAiSpecs((a) => ({ ...a, loading: false })); }
  };
  const discardAiSpecs = () => {
    const next = { ...(form.specs || {}) };
    aiSpecs.keys.forEach((k) => { delete next[k]; });
    set("specs", next);
    setAiSpecs({ keys: [], confidence: {}, notes: "", loading: false });
  };

  // Alendra · Tienda en línea: slogan, usos ideales, badges y prioridad a
  // partir de lo capturado en las demás secciones.
  const EMPTY_AI_STORE = { loading: false, applied: false, prev: null, alts: [], reasons: {}, notes: "", fields: [] };
  const [aiStore, setAiStore] = useState(EMPTY_AI_STORE);
  const suggestStorefront = async () => {
    if (aiStore.loading) return;
    const form = formRef.current;
    if (!form.name?.trim()) { toast.error("Escribe el nombre del producto para sugerir la tienda."); return false; }
    setAiStore((a) => ({ ...a, loading: true }));
    try {
      const payload = {
        name: form.name, brand: form.brand, category: form.category, characteristics: form.characteristics,
        condition: form.condition, aesthetic_grade: form.aesthetic_grade, aesthetic_notes: form.aesthetic_notes,
        specs: form.specs || {}, store_price: Number(form.store_price) || 0, compare_at_price: Number(form.compare_at_price) || 0,
        stock: Number(form.stock) || 0, free_shipping: !!form.free_shipping,
        short_tagline: form.short_tagline || "", use_cases: form.use_cases || [], badges: form.badges || [],
      };
      const { data } = await api.post("/alendra/suggest-storefront-form", payload);
      const extraKeys = [];
      for (const n of data.new_use_cases || []) {
        try { await api.post("/products/use-cases", n); extraKeys.push(n.key); } catch { /* ya existe */ }
      }
      if (extraKeys.length) reloadUseCases();
      const prev = { short_tagline: form.short_tagline || "", use_cases: form.use_cases || [], badges: form.badges || [], storefront_priority: form.storefront_priority || 0 };
      const hadTagline = !!(form.short_tagline || "").trim();
      const alts = hadTagline && data.short_tagline ? [data.short_tagline, ...(data.tagline_alternatives || [])] : (data.tagline_alternatives || []);
      const fields = [];
      if (!hadTagline && data.short_tagline) fields.push("slogan");
      if ((data.use_cases || []).length || extraKeys.length) fields.push("usos");
      if ((data.badges || []).length) fields.push("badges");
      if (!prev.storefront_priority) fields.push("prioridad");
      setForm((s) => ({
        ...s,
        short_tagline: hadTagline ? s.short_tagline : (data.short_tagline || s.short_tagline),
        use_cases: [...new Set([...(s.use_cases || []), ...(data.use_cases || []), ...extraKeys])],
        badges: [...new Set([...(s.badges || []), ...(data.badges || [])])],
        storefront_priority: s.storefront_priority ? s.storefront_priority : data.storefront_priority,
      }));
      setAiStore({ loading: false, applied: true, prev, alts, reasons: data.reasons || {}, notes: data.notes || "", fields });
      toast.success(fields.length ? `Alendra sugirió ${fields.join(", ")}. Revísalo antes de guardar ✨` : "Alendra revisó la sección: ya estaba completa ✨");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo sugerir la tienda en línea");
      setAiStore((a) => ({ ...a, loading: false }));
      return false;
    }
  };
  const discardAiStore = () => {
    if (aiStore.prev) setForm((s) => ({ ...s, ...aiStore.prev }));
    setAiStore(EMPTY_AI_STORE);
  };

  // Alendra · Precios: aprende del histórico (productos similares, ventas reales, mercado).
  const PRICE_KEYS = ["min_price", "reseller_price", "marketplace_price", "store_price"];
  const EMPTY_AI_PRICES = { loading: false, data: null, prev: null, applied: [] };
  const [aiPrices, setAiPrices] = useState(EMPTY_AI_PRICES);
  const applyPrices = (data, mode) => {
    const f = formRef.current;
    const prev = Object.fromEntries([...PRICE_KEYS, "compare_at_price"].map((k) => [k, f[k]]));
    const applied = PRICE_KEYS.filter((k) => mode === "all" || !(Number(f[k]) > 0));
    setForm((s) => ({
      ...s,
      ...Object.fromEntries(applied.map((k) => [k, data.prices[k]])),
      compare_at_price: (mode === "all" || !(Number(s.compare_at_price) > 0)) && data.compare_at_price > 0 ? data.compare_at_price : s.compare_at_price,
    }));
    setAiPrices((a) => ({ ...a, prev: a.prev || prev, applied }));
    return applied;
  };
  const suggestPrices = async () => {
    if (aiPrices.loading) return false;
    const f = formRef.current;
    if (!f.name?.trim()) { toast.error("Escribe el nombre del producto para sugerir precios."); return false; }
    setAiPrices((a) => ({ ...a, loading: true }));
    try {
      const payload = {
        name: f.name, product_id: product?.id || undefined, brand: f.brand, category: f.category, characteristics: f.characteristics,
        condition: f.condition, specs: f.specs || {}, cost: Number(f.cost) || 0, cost_currency: f.cost_currency, sale_currency: f.sale_currency,
        stock: Number(f.stock) || 0, ...Object.fromEntries(PRICE_KEYS.map((k) => [k, Number(f[k]) || 0])),
      };
      const { data } = await api.post("/alendra/suggest-prices-form", payload);
      setAiPrices({ loading: false, data, prev: null, applied: [] });
      const applied = applyPrices(data, "empty");
      toast.success(applied.length ? `Alendra propuso ${applied.length} precio(s) con base en ${data.comparables.length} comparable(s) ✨` : "Alendra revisó tus precios: compáralos con la sugerencia ✨");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudieron sugerir precios");
      setAiPrices((a) => ({ ...a, loading: false }));
      return false;
    }
  };
  const applyAllPrices = () => aiPrices.data && applyPrices(aiPrices.data, "all");
  const applyOnePrice = (k) => {
    if (!aiPrices.data) return;
    const f = formRef.current;
    setAiPrices((a) => ({ ...a, prev: a.prev || Object.fromEntries([...PRICE_KEYS, "compare_at_price"].map((x) => [x, f[x]])), applied: [...new Set([...a.applied, k])] }));
    set(k, aiPrices.data.prices[k]);
  };
  const discardAiPrices = () => {
    if (aiPrices.prev) setForm((s) => ({ ...s, ...aiPrices.prev }));
    setAiPrices(EMPTY_AI_PRICES);
  };

  // Un clic: Alendra completa lo que falte (ficha → precios → tienda) sobre el formulario.
  const [completingAll, setCompletingAll] = useState(false);
  const completeWithAlendra = async () => {
    if (completingAll) return;
    const f = formRef.current;
    if (!f.name?.trim()) { toast.error("Escribe el nombre del producto primero."); return; }
    setCompletingAll(true);
    try {
      const before = storefrontReadiness(f, specTemplate);
      const needSpecs = specTemplate.some((s) => s.required && !String(f.specs?.[s.key] || "").trim());
      if (needSpecs) await suggestSpecs();
      if (!(Number(f.store_price) > 0)) await suggestPrices();
      const g = formRef.current;
      if (!(g.short_tagline || "").trim() || !(g.use_cases || []).length) await suggestStorefront();
      const after = storefrontReadiness(formRef.current, specTemplate);
      if (after.filled <= before.filled) {
        const manual = after.missing.map((m) => m.label.replace("Ficha técnica: ", "")).join(", ");
        toast.info(`Alendra revisó el producto y no pudo completar más. Falta a mano: ${manual}`);
      } else if (!after.ready) {
        toast.info(`Preparación tienda ${after.filled}/${after.total}. Falta a mano: ${after.missing.map((m) => m.label.replace("Ficha técnica: ", "")).join(", ")}`);
      }
    } finally { setCompletingAll(false); }
  };

  const [extraSpecs, setExtraSpecs] = useState([]); // client-only, no-schema-locked
  const setSpec = (k, v) => set("specs", { ...(form.specs || {}), [k]: v });
  const addExtraSpec = () => setExtraSpecs([...extraSpecs, { key: "", value: "" }]);
  const updateExtraSpec = (i, patch) => {
    const next = [...extraSpecs];
    const before = next[i];
    next[i] = { ...before, ...patch };
    setExtraSpecs(next);
    // reflect on form.specs (drop old key if renamed)
    const specs = { ...(form.specs || {}) };
    if (before.key && (patch.key !== undefined) && patch.key !== before.key) delete specs[before.key];
    const finalKey = (patch.key !== undefined ? patch.key : before.key).trim();
    const finalVal = (patch.value !== undefined ? patch.value : before.value);
    if (finalKey) specs[finalKey] = finalVal;
    set("specs", specs);
  };
  const removeExtraSpec = (i) => {
    const e = extraSpecs[i];
    setExtraSpecs(extraSpecs.filter((_, j) => j !== i));
    if (e?.key) {
      const specs = { ...(form.specs || {}) };
      delete specs[e.key];
      set("specs", specs);
    }
  };

  // Derivamos badges automáticos para la vista previa: promo (compare>store),
  // envío gratis (flag), últimas piezas (stock<=3), reacondicionado, nuevo.
  const derivedBadges = useMemo(() => {
    const out = [];
    const store = Number(form.store_price || 0);
    const cmp = Number(form.compare_at_price || 0);
    if (cmp > 0 && store > 0 && cmp > store) out.push("promo");
    if (form.free_shipping) out.push("envio_gratis");
    if (Number(form.stock || 0) > 0 && Number(form.stock || 0) <= 3) out.push("ultimas_piezas");
    if (form.condition === "nuevo") out.push("nuevo");
    if (form.condition === "reacondicionado") out.push("reacondicionado");
    return out;
  }, [form.store_price, form.compare_at_price, form.free_shipping, form.stock, form.condition]);

  const toggleBadge = (key) => {
    const has = (form.badges || []).includes(key);
    set("badges", has ? form.badges.filter((b) => b !== key) : [...(form.badges || []), key]);
  };

  // Readiness (mismos criterios que el backend)
  const readiness = useMemo(() => storefrontReadiness(form, specTemplate), [form, specTemplate]);

  // Publicar: guarda primero lo capturado (para que el servidor evalúe lo mismo que ves)
  // y si falta algo lo dice en rojo y te lleva al campo.
  const publishToStorefront = async () => {
    if (!product?.id) { toast.error("Guarda el producto primero"); return; }
    const publish = !form.show_in_storefront;
    if (publish && !readiness.ready) {
      toast.error(`Falta: ${readiness.missing.map((m) => m.label).join(", ")}`);
      jumpToMissing(readiness.missing[0].key);
      return;
    }
    try {
      if (publish) await api.put(`/products/${product.id}`, buildPayload());
      await api.post(`/products/${product.id}/publish-storefront`, { publish });
      set("show_in_storefront", publish);
      toast.success(publish ? "Publicado en la tienda" : "Ocultado de la tienda");
      onSaved?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        toast.error(`Falta: ${detail.missing.map((m) => m.label).join(", ")}`);
        jumpToMissing(detail.missing[0]?.key || "store");
      } else {
        toast.error(detail || "No se pudo publicar");
      }
    }
  };

  useEffect(() => {
    if (!isNew) return;
    api.get("/products").then((r) => setAllProducts(r.data || [])).catch(() => {});
  }, [isNew]);

  // Hide the dropdown when clicking outside the name field.
  useEffect(() => {
    const onClickAway = (e) => {
      if (!nameWrapRef.current) return;
      if (!nameWrapRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  const matches = useMemo(() => {
    if (!isNew) return [];
    const q = (form.name || "").trim().toLowerCase();
    if (q.length < 2) return [];
    return allProducts
      .filter((p) => {
        const hay = `${p.name || ""} ${p.brand || ""} ${p.sku || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  }, [form.name, allProducts, isNew]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const createCategory = async () => {
    const name = newCatName.trim();
    if (!name) return;
    setCreatingCat(true);
    try {
      await api.post("/categories", { name });
      toast.success(`Categoría "${name}" creada`);
      set("category", name);
      setNewCatName("");
      setShowNewCat(false);
      if (onCategoriesChanged) await onCategoriesChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear categoría");
    } finally {
      setCreatingCat(false);
    }
  };

  const buildPayload = () => {
    const photoUrl = form.photos_url && form.photos_url.startsWith("data:") ? form.photos_url : ensureHttps(form.photos_url);
    // When the user uploads a new photo it becomes the MAIN photo (position 0).
    // ALL previously-existing photos are preserved after it so nothing is lost.
    // If photos_url is unchanged (still equal to existingPhotos[0]) we keep
    // the array as-is; if the user cleared photos_url we drop only the main.
    const existingPhotos = Array.isArray(form.photos) ? form.photos.filter(Boolean) : [];
    let photos;
    if (!photoUrl) {
      // User cleared the main photo → keep the rest as fallbacks.
      photos = existingPhotos.slice(1);
    } else if (existingPhotos.length === 0) {
      photos = [photoUrl];
    } else if (existingPhotos[0] === photoUrl) {
      // No change to main photo, keep entire array intact.
      photos = existingPhotos;
    } else {
      // New photo uploaded — put it at position 0 and preserve ALL previous
      // photos after it (including the old main). De-dupe just in case the
      // uploaded URL already existed somewhere in the array.
      const rest = existingPhotos.filter((u) => u !== photoUrl);
      photos = [photoUrl, ...rest];
    }
    const payload = {
      ...form,
      photos_url: photoUrl,
      photos,
      cover_photo: (form.cover_photo || "").trim() || null,
      shipping_cost_mxn: form.shipping_cost_mxn === "" || form.shipping_cost_mxn === null ? null : (parseFloat(form.shipping_cost_mxn) || 0),
      shipping_cost_bc_mxn: form.shipping_cost_bc_mxn === "" || form.shipping_cost_bc_mxn === null ? null : (parseFloat(form.shipping_cost_bc_mxn) || 0),
      condition_notes: (form.condition_notes || "").trim() || null,
      stock: parseInt(form.stock) || 0,
      cost: parseFloat(form.cost) || 0,
      min_price: parseFloat(form.min_price) || 0,
      reseller_price: parseFloat(form.reseller_price) || 0,
      marketplace_price: parseFloat(form.marketplace_price) || 0,
      store_price: parseFloat(form.store_price) || 0,
      low_stock_threshold: parseInt(form.low_stock_threshold) || 0,
      // Normalize aesthetic fields: "" → null; strip photos when no grade.
      aesthetic_grade: form.aesthetic_grade || null,
      aesthetic_photos: form.aesthetic_grade ? (form.aesthetic_photos || []).filter(Boolean) : [],
      aesthetic_notes: form.aesthetic_grade ? (form.aesthetic_notes || "") : "",
    };
    return payload;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    let savedId = product?.id;
    try {
      const payload = buildPayload();
      if (draftMode) {
        await onSubmitDraft(payload);
        toast.success("Ficha guardada");
      } else if (product) {
        await api.put(`/products/${product.id}`, payload);
        toast.success("Producto actualizado");
      } else {
        const { data } = await api.post("/products", payload);
        savedId = data?.id;
        toast.success("Producto creado");
      }
      // Quedó 100% listo y aún no está en la tienda → ofrecer publicarlo.
      if (!draftMode && savedId && readiness.ready && !form.show_in_storefront && (parseInt(form.stock) || 0) > 0) {
        setPublishOffer({ id: savedId, name: form.name });
        return;
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const [publishOffer, setPublishOffer] = useState(null);
  const [publishingOffer, setPublishingOffer] = useState(false);
  const skipPublishOffer = () => { setPublishOffer(null); onSaved(); };
  const confirmPublishOffer = async () => {
    if (!publishOffer) return;
    setPublishingOffer(true);
    try {
      await api.post(`/products/${publishOffer.id}/publish-storefront`, { publish: true });
      toast.success(`"${publishOffer.name}" ya está visible en la tienda 🎉`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.missing ? `Faltan: ${detail.missing.map((m) => m.label).join(", ")}` : (detail || "No se pudo publicar"));
    } finally {
      setPublishingOffer(false);
      setPublishOffer(null);
      onSaved();
    }
  };

  const STEPS = [
    { id: "basic", n: 1, icon: Package, title: "Identificación", subtitle: "Nombre, categoría, marca, condición", badge: "Obligatorio" },
    { id: "specs", n: 2, icon: ListChecks, title: "Ficha técnica", subtitle: "Specs por categoría y características", badge: canUseAlendra ? "IA" : null },
    { id: "stock", n: 3, icon: CurrencyDollar, title: "Inventario y precios", subtitle: "Stock, costo y lista de precios", badge: canUseAlendra ? "IA" : "Financiero" },
    { id: "photos", n: 4, icon: Camera, title: "Fotos", subtitle: "Foto principal y galería", badge: null },
    { id: "store", n: 5, icon: Storefront, title: "Tienda en línea", subtitle: "Slogan, usos, badges y publicación", badge: canUseAlendra ? "IA" : "E-commerce" },
  ];
  const hasPhoto = !!form.photos_url || !!(form.cover_photo || "").trim() || (Array.isArray(form.photos) && form.photos.length > 0);
  const stepDone = {
    basic: !!(form.name || "").trim() && !!(form.category || "").trim(),
    specs: specTemplate.length > 0 ? specProgress.complete : !!(form.characteristics || "").trim(),
    stock: Number(form.store_price) > 0 && Number(form.cost) > 0,
    photos: hasPhoto,
    store: readiness.ready,
  };
  const MISSING_TO_STEP = { name: "basic", category: "basic", store_price: "stock", photos: "photos", use_cases: "store", short_tagline: "store", specs: "specs" };
  const goTo = (id) => document.getElementById(`pf-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const jumpToMissing = (key) => goTo(key.startsWith("spec_") ? "specs" : (MISSING_TO_STEP[key] || "store"));
  const missingHint = (m) => {
    if (!m) return "";
    if (m.key.startsWith("spec_")) return `Llena "${m.label.replace("Ficha técnica: ", "")}" en Ficha técnica (paso 2)`;
    return {
      name: "Escribe el nombre en Identificación (paso 1)",
      category: "Elige la categoría en Identificación (paso 1)",
      store_price: "Captura el precio de tienda en Inventario y precios (paso 3)",
      photos: "Sube al menos una foto en el paso 4",
      use_cases: "Elige al menos un uso ideal aquí abajo",
      short_tagline: "Escribe el slogan corto aquí abajo",
      specs: "Captura al menos un dato en Ficha técnica (paso 2) o usa “Agregar spec libre”",
    }[m.key] || "Completa este dato";
  };
  const sameCurrency = form.cost_currency === form.sale_currency;
  const marginHint = (price) => {
    const c = Number(form.cost) || 0, p = Number(price) || 0;
    if (!sameCurrency || c <= 0 || p <= 0) return null;
    const pct = Math.round(((p - c) / p) * 100);
    return <span className={`text-[10px] font-mono-data ${pct < 0 ? "text-[#E0000F]" : pct < 15 ? "text-amber-600" : "text-emerald-700"}`}>margen {pct}%</span>;
  };
  // Chip "IA $X" bajo cada precio cuando la sugerencia difiere del valor capturado.
  const aiChip = (k) => {
    const v = aiPrices.data?.prices?.[k];
    if (!(v > 0) || Number(form[k]) === Number(v)) return null;
    return (
      <button type="button" onClick={() => applyOnePrice(k)} className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-mono-data text-[#7E57C2] border border-[#7E57C2]/30 bg-white rounded-sm px-1 hover:bg-[#7E57C2]/10" title="Usar el precio sugerido por Alendra" data-testid={`form-price-ai-chip-${k}`}>
        <Sparkle size={9} weight="fill" /> ${Number(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}
      </button>
    );
  };
  const aiBusy = completingAll || aiSpecs.loading || aiStore.loading || aiPrices.loading;
  const title = draftMode ? (draftTitle || "Ficha de preinventario") : product ? "Editar producto" : "Nuevo producto";

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[96vw] h-[90vh] max-h-[920px] flex flex-col p-0 gap-0 overflow-hidden" data-testid="product-form-dialog">
        <form onSubmit={submit} className="flex flex-col h-full min-h-0">
          {/* ── Header fijo ── */}
          <div className="h-14 px-6 border-b border-zinc-200 bg-white/95 backdrop-blur-md flex items-center justify-between gap-4 shrink-0 z-20">
            <DialogHeader className="p-0 space-y-0 text-left">
              <DialogTitle className="font-heading text-xl tracking-tight">{title}</DialogTitle>
              <DialogDescription className="sr-only">Formulario de producto organizado en 5 secciones: identificación, ficha técnica, inventario y precios, fotos y tienda en línea.</DialogDescription>
            </DialogHeader>
            <div className="hidden sm:flex items-center gap-2.5 mr-8" data-testid="form-readiness-header" title={readiness.ready ? "Listo para publicar en tienda" : `Falta: ${readiness.missing.map((m) => m.label).join(", ")}`}>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Preparación tienda</span>
              <div className="w-28 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                <div className={`h-full transition-[width] duration-300 ${readiness.ready ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round(readiness.filled / readiness.total * 100)}%` }} />
              </div>
              <span className="text-xs font-mono-data font-bold text-zinc-800">{readiness.filled}/{readiness.total}</span>
              {canUseAlendra && !readiness.ready && !draftMode && (
                <button type="button" onClick={completeWithAlendra} disabled={aiBusy}
                  className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-[#7E57C2] hover:bg-[#6a46ad] disabled:opacity-60"
                  title="Alendra completa ficha técnica, precios y tienda en línea (solo lo vacío). Revisa y guarda." data-testid="form-complete-ai">
                  {aiBusy ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} Completar con Alendra
                </button>
              )}
            </div>
          </div>

          {/* ── Cuerpo: índice + contenido ── */}
          <div className="flex-1 flex overflow-hidden min-h-0">
            <aside className="hidden md:flex w-60 border-r border-zinc-200 bg-zinc-50/80 p-3 flex-col justify-between overflow-y-auto shrink-0 select-none">
              <nav className="space-y-1" aria-label="Secciones del formulario" data-testid="form-steps">
                {STEPS.map((s) => {
                  const done = stepDone[s.id];
                  const active = activeStep === s.id;
                  return (
                    <button type="button" key={s.id} onClick={() => goTo(s.id)}
                      className={`w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md border-l-2 transition-[background-color,border-color] ${active ? "bg-white border-[#002FA7] shadow-sm" : "border-transparent hover:bg-white/70"}`}
                      data-testid={`form-step-${s.id}`}>
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-heading font-extrabold shrink-0 ${done ? "bg-emerald-500 text-white" : active ? "bg-[#002FA7] text-white" : "bg-zinc-200 text-zinc-600"}`}>
                        {done ? <Check size={12} weight="bold" /> : s.n}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className={`block text-sm font-semibold leading-tight ${active ? "text-[#002FA7]" : "text-zinc-800"}`}>{s.title}</span>
                        <span className="block text-[10px] text-zinc-500 truncate">{s.subtitle}</span>
                      </span>
                    </button>
                  );
                })}
              </nav>
              <div className="text-[10px] text-zinc-500 px-1 pt-3 mt-3 border-t border-zinc-200 space-y-1">
                <div><b className="text-zinc-700">*</b> obligatorio para guardar</div>
                <div><span className="text-[#E0000F] font-bold">*</span> en ficha técnica: obligatorio para tienda</div>
                <div className="flex items-center gap-1"><Check size={10} weight="bold" className="text-emerald-600" /> sección completa</div>
              </div>
            </aside>

            <div ref={contentRef} className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 bg-zinc-50/40 scroll-smooth min-w-0" data-testid="form-content">
              {/* índice compacto en pantallas chicas */}
              <div className="md:hidden flex gap-1.5 overflow-x-auto -mx-5 px-5 pb-1">
                {STEPS.map((s) => (
                  <button type="button" key={s.id} onClick={() => goTo(s.id)} className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${activeStep === s.id ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-zinc-200 text-zinc-600"}`} data-testid={`form-step-chip-${s.id}`}>
                    {stepDone[s.id] ? "✓ " : `${s.n}. `}{s.title}
                  </button>
                ))}
              </div>

              {/* ═══ 1. Identificación ═══ */}
              <FormSection done={stepDone.basic} id="basic" n={1} icon={Package} title="Identificación" subtitle="Lo mínimo para dar de alta el producto. Escribe el nombre y te avisamos si ya existe.">
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-4">
                  <div className="sm:col-span-6">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Nombre del producto *</Label>
                    <div className="relative mt-1" ref={nameWrapRef}>
                      <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                      <Input
                        value={form.name}
                        onChange={(e) => { set("name", e.target.value); if (isNew) setShowSuggestions(true); }}
                        onFocus={() => { if (isNew && (form.name || "").trim().length >= 2) setShowSuggestions(true); }}
                        required
                        autoFocus={isNew}
                        data-testid="form-name"
                        className="pl-8 h-10 text-base"
                        placeholder={isNew ? "Ej. Dell Latitude 7480 i5 8ª gen 16 GB 512 GB" : ""}
                      />
                      {isNew && showSuggestions && matches.length > 0 && (
                        <div className="absolute z-40 left-0 right-0 mt-1 bg-white border border-zinc-200 rounded shadow-lg max-h-72 overflow-y-auto" data-testid="dup-suggestions">
                          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
                            Productos parecidos ya registrados — haz click para hacer restock
                          </div>
                          {matches.map((p) => (
                            <button
                              type="button"
                              key={p.id}
                              onClick={() => { setShowSuggestions(false); if (onSelectExisting) onSelectExisting(p); }}
                              className="w-full flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0 text-left"
                              data-testid={`dup-match-${p.id}`}
                            >
                              <span className="w-9 h-9 rounded bg-zinc-100 flex items-center justify-center overflow-hidden shrink-0">
                                {p.photos_url || (p.photos && p.photos[0]) ? (
                                  <img src={p.photos_url || p.photos[0]} alt="" className="w-full h-full object-cover" />
                                ) : <Package size={16} className="text-zinc-400" />}
                              </span>
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm font-medium text-zinc-800 truncate">{p.name}</span>
                                <span className="block text-[11px] text-zinc-500 truncate">{[p.brand, p.category, p.sku].filter(Boolean).join(" · ")}</span>
                              </span>
                              <span className="text-xs font-mono-data shrink-0">
                                <span className={`px-2 py-0.5 rounded ${p.stock > 0 ? "bg-[#00873D]/10 text-[#00873D]" : "bg-[#E0000F]/10 text-[#E0000F]"}`}>stock: {p.stock}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Categoría *</Label>
                    {!showNewCat ? (
                      <div className="flex gap-1 mt-1">
                        <Select value={form.category} onValueChange={(v)=>set("category", v)}>
                          <SelectTrigger className="flex-1" data-testid="form-category"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                          <SelectContent>
                            {categories.map((c)=><SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Button type="button" variant="outline" onClick={()=>setShowNewCat(true)} className="px-2 shrink-0" title="Agregar nueva categoría" data-testid="form-category-new">+ Nueva</Button>
                      </div>
                    ) : (
                      <div className="flex gap-1 mt-1">
                        <Input
                          autoFocus
                          value={newCatName}
                          onChange={(e)=>setNewCatName(e.target.value)}
                          onKeyDown={(e)=>{ if (e.key === "Enter") { e.preventDefault(); createCategory(); } if (e.key === "Escape") { setShowNewCat(false); setNewCatName(""); } }}
                          placeholder="Nombre de la nueva categoría"
                          className="flex-1"
                          data-testid="form-category-new-input"
                        />
                        <Button type="button" onClick={createCategory} disabled={creatingCat || !newCatName.trim()} className="bg-[#002FA7] hover:bg-[#002277] text-white px-3 shrink-0" data-testid="form-category-new-submit">{creatingCat ? "…" : "Crear"}</Button>
                        <Button type="button" variant="outline" onClick={()=>{ setShowNewCat(false); setNewCatName(""); }} className="px-3 shrink-0">Cancelar</Button>
                      </div>
                    )}
                    <p className="text-[10px] text-zinc-400 mt-1">La categoría define la ficha técnica y los filtros de la tienda.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Marca</Label>
                    <BrandCombobox
                      value={form.brand || ""}
                      brands={brands}
                      open={brandOpen}
                      setOpen={setBrandOpen}
                      search={brandSearch}
                      setSearch={setBrandSearch}
                      onSelect={(name) => { set("brand", name); setBrandOpen(false); setBrandSearch(""); }}
                      onCreate={(name) => {
                        const trimmed = name.trim();
                        if (!trimmed) return;
                        if (!brands.some((b) => b.name.toLowerCase() === trimmed.toLowerCase())) {
                          setBrands((prev) => [...prev, { name: trimmed, count: 0 }].sort((a, b) => a.name.localeCompare(b.name)));
                        }
                        set("brand", trimmed);
                        setBrandOpen(false);
                        setBrandSearch("");
                      }}
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">SKU</Label>
                    <Input value={form.sku} onChange={(e)=>set("sku", e.target.value)} data-testid="form-sku" className="mt-1" placeholder="Opcional" />
                  </div>
                  <div className="sm:col-span-6">
                    <ConditionBlock form={form} set={set} />
                  </div>
                </div>
              </FormSection>

              {/* ═══ 2. Ficha técnica ═══ */}
              <FormSection done={stepDone.specs} id="specs" n={2} icon={ListChecks} title="Ficha técnica y características" subtitle={form.category ? `Campos de ${form.category}. Alimentan la ficha, los chips y los filtros de la tienda.` : "Elige primero una categoría para ver sus campos."}
                right={(
                  <div className="flex items-center gap-2">
                    {specProgress.hasSchema && specProgress.total > 0 && (
                      <span className={`text-[10px] font-mono-data font-bold px-2 py-0.5 rounded-sm ${specProgress.complete ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="form-spec-progress">
                        {specProgress.filled}/{specProgress.total} obligatorios
                      </span>
                    )}
                    {canUseAlendra && specTemplate.length > 0 && (
                      <button type="button" onClick={suggestSpecs} disabled={aiSpecs.loading}
                        className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-gradient-to-br from-[#002FA7] to-[#003ed4] hover:shadow-md disabled:opacity-50 disabled:cursor-wait"
                        title="Alendra AI sugiere los campos vacíos a partir del nombre y las características" data-testid="form-spec-ai-btn">
                        {aiSpecs.loading ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} Sugerir con IA
                      </button>
                    )}
                    {aiSpecs.keys.length > 0 && (
                      <button type="button" onClick={discardAiSpecs} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-zinc-600 border border-zinc-200 hover:bg-zinc-50" data-testid="form-spec-ai-discard">
                        <XIcon size={11} /> Descartar IA
                      </button>
                    )}
                  </div>
                )}>
                <div data-testid="form-spec-section">
                  {specTemplate.length > 0 ? (
                    <>
                      <SpecFields fields={specTemplate} values={form.specs || {}} onChange={setSpec} compact showMissing testidPrefix="form-spec" aiKeys={aiSpecs.keys} aiConfidence={aiSpecs.confidence} columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" />
                      {aiSpecs.notes && <div className="text-[10px] text-[#7E57C2] mt-1.5 flex items-start gap-1" data-testid="form-spec-ai-notes"><Sparkle size={10} weight="fill" className="mt-0.5 shrink-0" />{aiSpecs.notes}</div>}
                      <div className="text-[10px] text-zinc-400 mt-1.5"><span className="text-[#E0000F] font-bold">*</span> obligatorio para publicar en tienda. Puedes escribir un valor libre o elegir una sugerencia.</div>
                    </>
                  ) : (
                    <div className="text-xs text-zinc-400 italic border border-dashed border-zinc-200 rounded-md p-4 text-center">
                      {!form.category ? "Selecciona una categoría en el paso 1." : specSchemas.ready ? "Esta categoría no tiene ficha técnica definida; usa las características o agrega specs libres." : "Cargando esquema…"}
                    </div>
                  )}
                  {extraSpecs.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">Specs libres</div>
                      {extraSpecs.map((s, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <Input placeholder="Etiqueta" value={s.key} onChange={(e) => updateExtraSpec(i, { key: e.target.value })} className="h-8 text-sm max-w-[180px]" />
                          <Input placeholder="Valor" value={s.value} onChange={(e) => updateExtraSpec(i, { value: e.target.value })} className="h-8 text-sm flex-1" />
                          <button type="button" onClick={() => removeExtraSpec(i)} className="text-zinc-400 hover:text-[#E0000F] p-1"><Trash size={13} weight="bold" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={addExtraSpec} className="mt-2 text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1">
                    <Plus size={11} weight="bold" /> Agregar spec libre
                  </button>
                </div>

                <div className="pt-3 border-t border-zinc-100">
                  <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Características (texto para la tienda)</Label>
                  <div className="relative mt-1">
                    <Textarea
                      value={form.characteristics}
                      onChange={(e)=>set("characteristics", e.target.value)}
                      data-testid="form-characteristics"
                      className="pr-12 font-mono text-[13px] leading-relaxed bg-white"
                      rows={6}
                      placeholder={"Pega o escribe con formato. Ejemplos:\n\n• RAM: 16GB DDR4\n• Almacenamiento: 512GB SSD\n• Pantalla: 14\" IPS Full HD\n\nDeja líneas en blanco para separar secciones."}
                      spellCheck={false}
                    />
                    {canUseAlendra && (
                      <AlendraInlineButton
                        productId={product?.id}
                        formData={{ name: form.name, brand: form.brand, category: form.category, characteristics: form.characteristics, condition: form.condition, aesthetic_grade: form.aesthetic_grade, sale_price: form.sale_price }}
                        onGenerated={(text) => set("characteristics", text)}
                      />
                    )}
                  </div>
                  <p className="mt-1 text-[10px] text-zinc-500">Usa <code>-</code> o <code>•</code> para viñetas, o <code>1.</code> para listas. Los saltos de línea se respetan en la tienda.</p>
                </div>
              </FormSection>

              {/* ═══ 3. Inventario y precios ═══ */}
              <FormSection done={stepDone.stock} id="stock" n={3} icon={CurrencyDollar} title="Inventario y precios" subtitle="Primero las monedas, luego el costo y la lista de precios. El margen se calcula sobre el precio.">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Stock inicial</Label>
                    <Input type="number" min="0" value={form.stock} onChange={(e)=>set("stock", e.target.value)} data-testid="form-stock" className="mt-1 numeric" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Umbral stock bajo</Label>
                    <Input type="number" min="0" value={form.low_stock_threshold} onChange={(e)=>set("low_stock_threshold", e.target.value)} data-testid="form-threshold" className="mt-1 numeric" />
                    <p className="text-[10px] text-zinc-400 mt-1">Te avisamos al llegar a esta cantidad.</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Moneda del costo</Label>
                    <Select value={form.cost_currency} onValueChange={(v)=>set("cost_currency", v)}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="form-cost-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD (Dólares)</SelectItem>
                        <SelectItem value="MXN">MXN (Pesos)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-zinc-400 mt-1">En qué moneda compras.</p>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Moneda de venta</Label>
                    <Select value={form.sale_currency} onValueChange={(v)=>set("sale_currency", v)}>
                      <SelectTrigger className="mt-1 bg-white" data-testid="form-sale-currency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD (Dólares)</SelectItem>
                        <SelectItem value="MXN">MXN (Pesos)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-zinc-400 mt-1">Aplica a los 4 precios.</p>
                  </div>
                </div>

                {canUseAlendra && (
                  <PriceAIPanel ai={aiPrices} currency={form.sale_currency} onSuggest={suggestPrices} onApplyAll={applyAllPrices} onDiscard={discardAiPrices} />
                )}

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 p-3 rounded-md bg-zinc-50 border border-zinc-200">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-700">Costo ({form.cost_currency})</Label>
                    <Input type="number" step="0.01" value={form.cost} onChange={(e)=>set("cost", e.target.value)} data-testid="form-cost" className="mt-1 numeric bg-white border-zinc-300" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Mínimo ({form.sale_currency})</Label>
                    <Input type="number" step="0.01" value={form.min_price} onChange={(e)=>set("min_price", e.target.value)} data-testid="form-min" className={`mt-1 numeric bg-white ${aiPrices.applied.includes("min_price") ? "ring-1 ring-[#7E57C2]/60" : ""}`} />
                    <div className="mt-0.5 h-3">{marginHint(form.min_price)}</div>
                    {aiChip("min_price")}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Reventa ({form.sale_currency})</Label>
                    <Input type="number" step="0.01" value={form.reseller_price} onChange={(e)=>set("reseller_price", e.target.value)} data-testid="form-reseller" className={`mt-1 numeric bg-white ${aiPrices.applied.includes("reseller_price") ? "ring-1 ring-[#7E57C2]/60" : ""}`} />
                    <div className="mt-0.5 h-3">{marginHint(form.reseller_price)}</div>
                    {aiChip("reseller_price")}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Distribuidor ({form.sale_currency})</Label>
                    <Input type="number" step="0.01" value={form.marketplace_price} onChange={(e)=>set("marketplace_price", e.target.value)} data-testid="form-marketplace" className={`mt-1 numeric bg-white ${aiPrices.applied.includes("marketplace_price") ? "ring-1 ring-[#7E57C2]/60" : ""}`} />
                    <div className="mt-0.5 h-3">{marginHint(form.marketplace_price)}</div>
                    {aiChip("marketplace_price")}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-[#002FA7]">Tienda ({form.sale_currency}) <span className="text-[#002FA7]">●</span></Label>
                    <Input type="number" step="0.01" value={form.store_price} onChange={(e)=>set("store_price", e.target.value)} data-testid="form-store" className={`mt-1 numeric bg-white border-[#002FA7]/40 font-semibold ${aiPrices.applied.includes("store_price") ? "ring-1 ring-[#7E57C2]/60" : ""}`} />
                    <div className="mt-0.5 h-3">{marginHint(form.store_price)}</div>
                    {aiChip("store_price")}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Precio "antes" ({form.sale_currency})</Label>
                    <Input type="number" step="0.01" min="0" value={form.compare_at_price || 0} onChange={(e) => set("compare_at_price", Number(e.target.value) || 0)} className="mt-1 numeric" data-testid="form-compare-price" />
                    <div className="text-[10px] text-zinc-400 mt-0.5">
                      {Number(form.compare_at_price || 0) > Number(form.store_price || 0) && Number(form.store_price || 0) > 0
                        ? <span className="text-emerald-700 font-semibold">-{Math.round((1 - Number(form.store_price) / Number(form.compare_at_price)) * 100)}% se muestra en tienda</span>
                        : "Deja en 0 si no hay descuento."}
                    </div>
                  </div>
                  <div className="sm:col-span-2 flex items-center gap-2 p-2.5 rounded-md border border-emerald-200 bg-emerald-50/40 mt-5">
                    <input id="form-free-shipping" type="checkbox" checked={!!form.free_shipping} onChange={(e)=>set("free_shipping", e.target.checked)} className="w-4 h-4 accent-emerald-600 cursor-pointer" data-testid="form-free-shipping" />
                    <label htmlFor="form-free-shipping" className="text-sm cursor-pointer select-none inline-flex items-center gap-2 flex-wrap">
                      <span className="text-emerald-700 font-semibold">🚚 Badge "Envío gratis"</span>
                      <span className="text-xs text-zinc-500">— solo visual en la tienda. El envío es gratis en Tijuana, Rosarito y Tecate; fuera de ahí se cobra la tarifa por zona de abajo.</span>
                    </label>
                  </div>
                </div>

                {/* Envío por zona + condiciones */}
                <div className="rounded-md border border-zinc-200 bg-white p-3 space-y-3" data-testid="form-shipping-block">
                  <div>
                    <div className="text-xs font-semibold text-zinc-800">🚚 Costo de envío por zona</div>
                    <p className="text-[11px] text-zinc-500">Tijuana, Rosarito y Tecate siempre es <b>gratis</b>. Si dejas un campo vacío se usa la tarifa general de Ajustes.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-600">Envío resto de México (MXN)</Label>
                      <Input type="number" min="0" step="1" placeholder="Ej. 600" value={form.shipping_cost_mxn} onChange={(e)=>set("shipping_cost_mxn", e.target.value)} className="mt-1 numeric" data-testid="form-shipping-national" />
                      <div className="text-[10px] text-zinc-400 mt-0.5">Sonora en adelante. Vacío = tarifa general de Ajustes.</div>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-600">Envío Baja California (MXN)</Label>
                      <Input type="number" min="0" step="1" placeholder="Opcional" value={form.shipping_cost_bc_mxn} onChange={(e)=>set("shipping_cost_bc_mxn", e.target.value)} className="mt-1 numeric" data-testid="form-shipping-bc" />
                      <div className="text-[10px] text-zinc-400 mt-0.5">Ensenada, Mexicali, San Quintín…</div>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-600">Notas / condiciones (visibles en la tienda)</Label>
                    <Textarea value={form.condition_notes} onChange={(e)=>set("condition_notes", e.target.value)} rows={2} placeholder="Ej. Envío asegurado por paquetería, 3-5 días hábiles. Incluye cargador, no incluye caja." className="mt-1 text-sm" data-testid="form-condition-notes" />
                  </div>
                </div>
              </FormSection>

              {/* ═══ 4. Fotos ═══ */}
              <FormSection done={stepDone.photos} id="photos" n={4} icon={Camera} title="Fotos" subtitle="Una foto principal basta para publicar; la portada (opcional) es la que abre en la tienda; la galería completa se gestiona en Marketing.">
                <CoverPhotoField value={form.cover_photo} gallery={Array.isArray(form.photos) ? form.photos : []} onChange={(v) => set("cover_photo", v)} />
                <div className="flex items-start gap-4 mt-3">
                  <button
                    type="button"
                    onClick={() => { if (hasPhoto) setLightboxOpen(true); }}
                    className="w-28 h-28 border border-zinc-200 rounded-md flex items-center justify-center bg-zinc-50 overflow-hidden flex-shrink-0 relative cursor-zoom-in disabled:cursor-default"
                    disabled={!hasPhoto}
                    data-testid="form-photo-preview"
                    aria-label="Ver foto en grande"
                  >
                    {form.photos_url ? (
                      <img src={form.photos_url.startsWith("data:") ? form.photos_url : ensureHttps(form.photos_url)} alt="Producto" className="max-w-full max-h-full object-contain" />
                    ) : (
                      <span className="text-[10px] text-zinc-400 flex flex-col items-center gap-1"><Camera size={18} /> sin foto</span>
                    )}
                    {Array.isArray(form.photos) && form.photos.length > 1 && (
                      <span className="absolute bottom-0.5 right-0.5 px-1 py-0.5 text-[9px] font-mono-data bg-black/70 text-white rounded">+{form.photos.length - 1}</span>
                    )}
                  </button>
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 8 * 1024 * 1024) { toast.error("Máx 8 MB"); return; }
                        try {
                          const result = await uploadPhoto(file, { folder: "alendrapc/products", maxSide: 1600, quality: 0.85 });
                          set("photos_url", result);
                        } catch (err) {
                          toast.error(err?.message || "No se pudo subir la imagen");
                        }
                      }}
                      className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#002FA7] file:text-white file:rounded file:cursor-pointer file:hover:bg-[#002277]"
                      data-testid="form-photo-upload"
                    />
                    <div className="text-[10px] text-zinc-400">…o pega un link (Drive, Cloudinary, etc.):</div>
                    <Input
                      type="text"
                      placeholder="https://drive.google.com/..."
                      value={form.photos_url && form.photos_url.startsWith("data:") ? "" : form.photos_url}
                      onChange={(e) => set("photos_url", e.target.value)}
                      data-testid="form-photos-url"
                      className="h-8 text-xs"
                    />
                    <div className="flex items-center gap-3">
                      {form.photos_url && (
                        <button type="button" onClick={() => set("photos_url", "")} className="text-[11px] text-[#E0000F] hover:underline">Quitar foto</button>
                      )}
                      {Array.isArray(form.photos) && form.photos.length > 1 && (
                        <span className="text-[10px] text-zinc-500">{form.photos.length} fotos en galería · gestiona el resto en <b>Marketing</b></span>
                      )}
                    </div>
                  </div>
                </div>
              </FormSection>

              {/* ═══ 5. Tienda en línea ═══ */}
              <FormSection done={stepDone.store} id="store" n={5} icon={Storefront} tint title="Tienda en línea" subtitle="Cómo se ve el producto en la tienda: slogan, usos ideales, badges y publicación." testid="storefront-enrichment"
                right={product?.id && (
                  <Button
                    type="button"
                    onClick={publishToStorefront}
                    className={form.show_in_storefront ? "bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" : readiness.ready ? "bg-[#002FA7] hover:bg-[#002277] text-white gap-1.5" : "bg-zinc-200 text-zinc-600 hover:bg-[#E0000F]/10 hover:text-[#E0000F] gap-1.5"}
                    title={!form.show_in_storefront && !readiness.ready ? `Falta: ${readiness.missing.map((m) => m.label).join(", ")}` : ""}
                    data-testid="storefront-publish-btn"
                  >
                    <Check size={13} weight="bold" />
                    {form.show_in_storefront ? "Publicado — quitar" : "Marcar como listo para tienda"}
                  </Button>
                )}>
                <div className="flex items-center gap-3 flex-wrap p-3 rounded-md bg-white border border-zinc-200" data-testid="form-readiness-panel">
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                      <div className={`h-full transition-[width] duration-300 ${readiness.ready ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${Math.round(readiness.filled / readiness.total * 100)}%` }} />
                    </div>
                    <div className="text-xs font-mono-data font-semibold text-zinc-700">{readiness.filled}/{readiness.total}</div>
                  </div>
                  {readiness.missing.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] uppercase tracking-widest text-[#E0000F] font-bold">Falta:</span>
                      {readiness.missing.map((m) => (
                        <button type="button" key={m.key} onClick={() => jumpToMissing(m.key)} className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#E0000F]/10 text-[#E0000F] border border-[#E0000F]/30 hover:bg-[#E0000F] hover:text-white transition-colors" title={`${missingHint(m)} · clic para ir al campo`} data-testid={`form-missing-${m.key}`}>
                          {m.label}
                        </button>
                      ))}
                      <span className="text-[10px] text-zinc-500 w-full sm:w-auto" data-testid="form-missing-hint">{missingHint(readiness.missing[0])}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-emerald-700 font-semibold inline-flex items-center gap-1"><Check size={12} weight="bold" /> Todo listo para publicar</span>
                  )}
                  {product?.id ? (
                    <div className="ml-auto flex items-center gap-2 text-sm">
                      <span className={`w-2.5 h-2.5 rounded-full ${form.show_in_storefront ? "bg-emerald-500" : "bg-zinc-300"}`} />
                      <span className="font-semibold">{form.show_in_storefront ? "Visible en tienda" : "Oculto de tienda"}</span>
                    </div>
                  ) : !draftMode && (
                    <span className="ml-auto text-[11px] text-zinc-500 italic" data-testid="form-publish-hint">Guarda el producto para poder publicarlo en la tienda</span>
                  )}
                </div>

                {canUseAlendra && (
                  <div className="rounded-md border border-[#7E57C2]/30 bg-[#7E57C2]/5 p-3" data-testid="form-store-ai">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="text-xs text-zinc-700 inline-flex items-start gap-1.5 min-w-0">
                        <Sparkle size={13} weight="fill" className="text-[#7E57C2] shrink-0 mt-0.5" />
                        <span><b className="text-[#7E57C2]">Alendra AI</b> propone slogan, usos ideales, badges y prioridad a partir de la ficha técnica, características, condición y precio.</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {aiStore.applied && (
                          <button type="button" onClick={discardAiStore} className="inline-flex items-center gap-1 h-7 px-2 rounded-md text-[11px] text-zinc-600 border border-zinc-200 hover:bg-zinc-50 bg-white" data-testid="form-store-ai-discard">
                            <XIcon size={11} weight="bold" /> Descartar sugerencias
                          </button>
                        )}
                        <button type="button" onClick={suggestStorefront} disabled={aiStore.loading}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-semibold text-white bg-[#7E57C2] hover:bg-[#6a46ad] disabled:opacity-60"
                          data-testid="form-store-ai-btn">
                          {aiStore.loading ? <Spinner size={12} className="animate-spin" /> : <Sparkle size={12} weight="fill" />} {aiStore.applied ? "Volver a sugerir" : "Sugerir con IA"}
                        </button>
                      </div>
                    </div>
                    {aiStore.alts.length > 0 && (
                      <div className="mt-2.5" data-testid="form-store-ai-alts">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Otras opciones de slogan (toca para usar)</div>
                        <div className="flex flex-wrap gap-1.5">
                          {aiStore.alts.map((a, i) => (
                            <button type="button" key={i} onClick={() => set("short_tagline", a)} className="text-left text-[11px] px-2 py-1 rounded-sm bg-white border border-[#7E57C2]/30 text-zinc-700 hover:border-[#7E57C2] hover:text-[#7E57C2]" data-testid={`form-store-ai-alt-${i}`}>
                              “{a}”
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {(aiStore.reasons.use_cases || aiStore.reasons.badges || aiStore.reasons.priority || aiStore.notes) && (
                      <ul className="mt-2.5 space-y-0.5 text-[11px] text-zinc-600" data-testid="form-store-ai-reasons">
                        {aiStore.reasons.use_cases && <li><b className="text-zinc-800">Usos:</b> {aiStore.reasons.use_cases}</li>}
                        {aiStore.reasons.badges && <li><b className="text-zinc-800">Badges:</b> {aiStore.reasons.badges}</li>}
                        {aiStore.reasons.priority && <li><b className="text-zinc-800">Prioridad:</b> {aiStore.reasons.priority}</li>}
                        {aiStore.notes && <li className="text-[#7E57C2]">{aiStore.notes}</li>}
                      </ul>
                    )}
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Slogan corto <span className="text-[#002FA7]">●</span></Label>
                  <Input value={form.short_tagline || ""} onChange={(e) => set("short_tagline", e.target.value.slice(0, 80))} placeholder="Ej: Para estudiar, trabajar o crear." className="mt-1 bg-white" data-testid="form-tagline" />
                  <div className="text-[10px] text-zinc-400 mt-0.5">{(form.short_tagline || "").length}/80</div>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Usos ideales <span className="text-[#002FA7]">●</span></Label>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {useCases.map((uc) => {
                      const active = (form.use_cases || []).includes(uc.key);
                      return (
                        <button type="button" key={uc.key} onClick={() => toggleUseCase(uc.key)}
                          className={`text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm transition-colors ${active ? "bg-[#002FA7] text-white" : "bg-white border border-zinc-300 text-zinc-600 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
                          data-testid={`form-usecase-${uc.key}`}>
                          {uc.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 items-center mt-2">
                    <Input value={newUseCase} onChange={(e) => setNewUseCase(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomUseCase(); } }} placeholder="Agregar uso ideal (ej: Servidor, Video llamadas)…" className="h-8 text-sm max-w-xs bg-white" data-testid="form-usecase-new" />
                    <Button type="button" onClick={addCustomUseCase} size="sm" variant="outline" className="h-8 gap-1"><Plus size={11} weight="bold" /> Agregar</Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Badges de la tarjeta</Label>
                    <div className="text-[10px] text-zinc-500 mt-0.5">Los automáticos salen de tu configuración; puedes fijar extras.</div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {DERIVED_BADGE_CANDIDATES.map((b) => {
                        const isDerived = derivedBadges.includes(b.key);
                        const isManual = (form.badges || []).includes(b.key);
                        const active = isDerived || isManual;
                        return (
                          <button type="button" key={b.key} onClick={() => toggleBadge(b.key)}
                            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm transition-[background-color,border-color] ${active ? "text-white" : "text-zinc-500 border border-dashed border-zinc-300 hover:border-zinc-500"}`}
                            style={active ? { background: b.color } : {}} data-testid={`form-badge-${b.key}`}>
                            {b.label}{isDerived && <span className="text-[8px] opacity-80">auto</span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Prioridad en destacados</Label>
                    <Input type="number" min="0" max="100" value={form.storefront_priority || 0} onChange={(e) => set("storefront_priority", Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0)))} className="mt-1 numeric max-w-[120px] bg-white" data-testid="form-priority" />
                    <div className="text-[10px] text-zinc-400 mt-0.5">0–100 · mayor aparece antes en "Ofertas destacadas".</div>
                  </div>
                </div>

                {canUseAlendra && product?.id && (
                  <details className="pt-3 border-t border-[#002FA7]/15 group" data-testid="form-alendra-panel">
                    <summary className="cursor-pointer text-xs font-semibold text-[#002FA7] inline-flex items-center gap-1.5 select-none list-none">
                      <Sparkle size={13} weight="fill" /> Alendra AI · descripción, SEO y análisis de precio
                      <CaretDown size={12} className="transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="pt-3">
                      <AlendraAI productId={product.id} product={product} onApplyDescription={(text) => set("characteristics", text)} />
                    </div>
                  </details>
                )}
              </FormSection>
            </div>
          </div>

          {/* ── Footer fijo ── */}
          <DialogFooter className="h-16 px-6 border-t border-zinc-200 bg-zinc-50/90 backdrop-blur-md flex flex-row items-center justify-between gap-2 shrink-0 z-20 sm:justify-between">
            {canSendToTaller ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setTallerOpen(true)}
                disabled={availableStock <= 0}
                title={availableStock <= 0 ? "Sin stock disponible para enviar al taller" : "Envía una unidad al Taller y Reparación"}
                className="gap-1.5 border-[#E0000F]/40 text-[#E0000F] hover:bg-[#E0000F]/5 hover:border-[#E0000F]"
                data-testid="send-to-taller-btn"
              >
                <FirstAidKit size={14} weight="bold" /> Enviar al taller
              </Button>
            ) : <span className="text-[11px] text-zinc-500 hidden sm:inline">{stepDone.basic ? "Puedes guardar en cualquier momento; la ficha se completa después." : "Nombre y categoría son lo único obligatorio para guardar."}</span>}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} data-testid="form-cancel">Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white px-5" data-testid="form-submit">
                {saving ? "Guardando…" : draftMode ? (draftSubmitLabel || "Guardar ficha") : product ? "Actualizar" : "Crear producto"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
      <PhotoLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        photos={[...((form.cover_photo || "").trim() ? [form.cover_photo.trim()] : []), ...(Array.isArray(form.photos) && form.photos.length > 0 ? form.photos : (form.photos_url ? [form.photos_url] : []))].filter((u, i, a) => u && a.indexOf(u) === i)}
        title={form.name || "Producto"}
        filenameBase={form.name || "producto"}
      />
      {/* Send-to-taller quick dialog */}
      <Dialog open={tallerOpen} onOpenChange={setTallerOpen}>
        <DialogContent className="max-w-md" data-testid="send-taller-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FirstAidKit size={20} weight="fill" className="text-[#E0000F]" />
              Enviar al Taller y Reparación
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="border border-zinc-200 bg-zinc-50 p-3 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Producto</div>
              <div className="font-semibold text-sm text-zinc-900 mt-0.5">{product?.name}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5 font-mono-data">
                SKU {product?.sku || "—"} · Stock disponible: {availableStock}
                {product?.hospital_qty ? ` (${product.hospital_qty} ya en taller)` : ""}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción de la falla *</Label>
              <Textarea
                value={tallerForm.fault_description}
                onChange={(e) => setTallerForm({ ...tallerForm, fault_description: e.target.value })}
                rows={4}
                placeholder="No enciende, teclado falla, pantalla muerta, errores de sistema…"
                className="mt-1"
                data-testid="taller-fault"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Ubicación física</Label>
              <Textarea
                value={tallerForm.location}
                onChange={(e) => setTallerForm({ ...tallerForm, location: e.target.value })}
                rows={2}
                placeholder="Mesa 2, Estante B, planta baja…"
                className="mt-1"
                data-testid="taller-location"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Prioridad</Label>
              <Select value={tallerForm.priority} onValueChange={(v) => setTallerForm({ ...tallerForm, priority: v })}>
                <SelectTrigger className="mt-1" data-testid="taller-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">🔴 Urgente</SelectItem>
                  <SelectItem value="high">🟠 Alta</SelectItem>
                  <SelectItem value="normal">🔵 Normal</SelectItem>
                  <SelectItem value="low">⚪ Baja</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTallerOpen(false)} disabled={tallerSending}>Cancelar</Button>
              <Button onClick={sendToTaller} disabled={tallerSending} className="bg-[#E0000F] hover:bg-[#c30414] text-white gap-1.5" data-testid="taller-submit">
                <FirstAidKit size={14} weight="bold" />
                {tallerSending ? "Enviando…" : "Enviar al taller"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PublishOfferDialog offer={publishOffer} onPublish={confirmPublishOffer} onSkip={skipPublishOffer} busy={publishingOffer} />
    </Dialog>
  );
}


function BrandCombobox({ value, brands, open, setOpen, search, setSearch, onSelect, onCreate }) {
  const norm = search.trim().toLowerCase();
  const filtered = norm
    ? brands.filter((b) => b.name.toLowerCase().includes(norm))
    : brands;
  const exact = brands.some((b) => b.name.trim().toLowerCase() === norm);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1 w-full flex items-center justify-between gap-2 rounded-md border border-zinc-300 bg-white px-3 h-9 text-sm hover:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
          data-testid="form-brand"
        >
          <span className={`truncate text-left ${value ? "" : "text-zinc-400"}`}>{value || "Selecciona o escribe una marca…"}</span>
          <CaretDown size={12} className="text-zinc-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="relative p-2 border-b border-zinc-100">
          <MagnifyingGlass size={12} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (search.trim() && !exact) onCreate(search);
                else if (filtered[0]) onSelect(filtered[0].name);
              }
            }}
            placeholder="Buscar o escribir nueva marca…"
            className="pl-7 h-8 text-sm"
            data-testid="form-brand-search"
          />
        </div>
        <div className="max-h-60 overflow-y-auto" data-testid="form-brand-list">
          {filtered.length === 0 && !search.trim() && (
            <div className="text-center text-xs text-zinc-400 py-4">No hay marcas todavía</div>
          )}
          {filtered.map((b) => {
            const isSel = b.name === value;
            return (
              <button
                type="button"
                key={b.name}
                onClick={() => onSelect(b.name)}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-zinc-50 border-b border-zinc-50 last:border-0"
                data-testid={`form-brand-opt-${b.name}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {isSel && <Check size={12} weight="bold" className="text-[#002FA7]" />}
                  <span className={isSel ? "font-semibold" : ""}>{b.name}</span>
                </span>
                <span className="text-[10px] font-mono text-zinc-400">{b.count}</span>
              </button>
            );
          })}
          {search.trim() && !exact && (
            <button
              type="button"
              onClick={() => onCreate(search)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm bg-[#002FA7]/5 hover:bg-[#002FA7]/10 text-[#002FA7] font-medium border-t border-zinc-100"
              data-testid="form-brand-create"
            >
              <Plus size={14} weight="bold" /> Crear marca: &quot;{search.trim()}&quot;
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}


/**
 * Bloque "Condición del equipo" + "Estética":
 *  - condition: Nuevo / Semi-nuevo / Reacondicionado (siempre visible)
 *  - aesthetic_grade: A (sin detalles) / B (detalles leves) / C (detalles notorios)
 *    Cuando se elige B o C aparece campo de notas + uploader de fotos para
 *    documentar las marcas del equipo. Esas fotos se muestran al cliente en
 *    la página del producto.
 */
const CONDITIONS = [
  { v: "nuevo",          label: "Nuevo",          color: "#00873D", desc: "Equipo nuevo, en su empaque original." },
  { v: "seminuevo",      label: "Semi-nuevo",     color: "#D17A00", desc: "Equipo poco usado, en excelente estado." },
  { v: "reacondicionado",label: "Reacondicionado",color: "#7E57C2", desc: "Equipo revisado y restaurado por nuestro equipo." },
];
const GRADES = [
  { v: "",  label: "Sin clasificar (estética perfecta)", color: "#71717a" },
  { v: "A", label: "Grado A — Sin detalles visibles",     color: "#00873D" },
  { v: "B", label: "Grado B — Detalles leves",            color: "#D17A00" },
  { v: "C", label: "Grado C — Detalles más visibles",     color: "#E0000F" },
];

function ConditionBlock({ form, set }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const grade = form.aesthetic_grade || "";
  const photos = Array.isArray(form.aesthetic_photos) ? form.aesthetic_photos.filter(Boolean) : [];

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        try {
          const url = await uploadPhoto(f, { folder: "alendrapc/products/aesthetic" });
          urls.push(url);
        } catch (err) {
          toast.error(err?.message || "Error subiendo imagen");
        }
      }
      if (urls.length) {
        set("aesthetic_photos", [...photos, ...urls]);
        toast.success(`${urls.length} foto${urls.length === 1 ? "" : "s"} agregada${urls.length === 1 ? "" : "s"}`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAt = (idx) => set("aesthetic_photos", photos.filter((_, i) => i !== idx));

  const condDef = CONDITIONS.find((c) => c.v === form.condition) || CONDITIONS[0];

  return (
    <div className="p-3 rounded-md border border-blue-200 bg-blue-50/40 space-y-3" data-testid="condition-block">
      <div className="flex items-center gap-2">
        <Package size={16} weight="duotone" className="text-[#002FA7]" />
        <span className="text-xs uppercase tracking-wider font-semibold text-[#002FA7]">Condición del equipo</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider">Condición *</Label>
          <select
            value={form.condition || "seminuevo"}
            onChange={(e) => set("condition", e.target.value)}
            className="mt-1 w-full h-9 border border-zinc-300 rounded-md px-2 text-sm bg-white"
            data-testid="form-condition"
          >
            {CONDITIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <p className="text-[11px] text-zinc-500 mt-1" style={{ color: condDef.color }}>{condDef.desc}</p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Estética (grado)</Label>
          <select
            value={grade}
            onChange={(e) => set("aesthetic_grade", e.target.value)}
            className="mt-1 w-full h-9 border border-zinc-300 rounded-md px-2 text-sm bg-white"
            data-testid="form-aesthetic-grade"
          >
            {GRADES.map((g) => <option key={g.v || "none"} value={g.v}>{g.label}</option>)}
          </select>
          <p className="text-[11px] text-zinc-500 mt-1">Selecciona un grado solo cuando el equipo tenga marcas o detalles.</p>
        </div>
      </div>

      {(grade === "B" || grade === "C") && (
        <div className="space-y-3 pt-1 border-t border-blue-200/60" data-testid="aesthetic-details">
          <div>
            <Label className="text-xs uppercase tracking-wider">Notas del detalle (opcional)</Label>
            <Textarea
              value={form.aesthetic_notes || ""}
              onChange={(e) => set("aesthetic_notes", e.target.value)}
              placeholder="Ej: ligero raspón en tapa trasera, marca de uso en bisel inferior…"
              className="mt-1 text-sm"
              rows={2}
              data-testid="form-aesthetic-notes"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider">Fotos de los detalles ({photos.length})</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(Array.from(e.target.files || []))}
                className="hidden"
                data-testid="form-aesthetic-photos-input"
              />
              <Button
                type="button" variant="outline" size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="gap-1.5"
                data-testid="form-aesthetic-upload-btn"
              >
                <Camera size={14} weight="bold" /> {uploading ? "Subiendo…" : "Agregar foto"}
              </Button>
            </div>
            {photos.length > 0 && (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="aesthetic-photos-grid">
                {photos.map((p, i) => (
                  <div key={p + i} className="relative group aspect-square rounded border border-zinc-200 overflow-hidden bg-zinc-50">
                    <img src={p} alt={`detalle ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAt(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full inline-flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Eliminar"
                      data-testid={`aesthetic-remove-${i}`}
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-zinc-500 mt-1">Estas fotos se muestran al cliente para que sepa exactamente qué detalles tiene el equipo.</p>
          </div>
        </div>
      )}
    </div>
  );
}
